import { ItemStatus } from "@focus/focusUI";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { catchError, map, mergeMap, of } from "rxjs";
import type {
  ApiKey,
  ApiKeyConfig,
  ApiKeysRequestKey,
  ApiKeysState,
} from "../types/apiKeys";
import { createAsyncReducers } from "./helpers";

const baseURL = "/api-keys/api/v1";

const initialState: ApiKeysState = {
  apiKeys: [],
  roles: [],
  lastCreatedApiKey: undefined,
  requests: {
    getApiKeys: { status: ItemStatus.READY, error: null },
    getRoles: { status: ItemStatus.READY, error: null },
    addApiKeys: { status: ItemStatus.READY, error: null },
    updateApiKeys: { status: ItemStatus.READY, error: null },
    deleteApiKeys: { status: ItemStatus.READY, error: null },
    deleteLasCreatedtApiKey: { status: ItemStatus.READY, error: null },
  },
};

export const apiKeysControllerMessages = {
  createSuccess: "API Key added successfully.",
  updateSuccess: "API Key updated successfully.",
  deleteSuccess: "API Key deleted successfully.",
  apiKeysLoadError: "Could not load API Keys.",
};

const getApiKeysAsync = createAsyncReducers<
  ApiKeysState,
  ApiKeysRequestKey,
  ApiKey[]
>("getApiKeys", (state, action) => {
  state.apiKeys = action.payload;
});

const getRolessAsync = createAsyncReducers<
  ApiKeysState,
  ApiKeysRequestKey,
  string[]
>("getRoles", (state, action) => {
  state.roles = action.payload;
});

const addApiKeysAsync = createAsyncReducers<
  ApiKeysState,
  ApiKeysRequestKey,
  ApiKey,
  { apiKey: ApiKeyConfig }
>("addApiKeys", (state, action) => {
  state.apiKeys = [...state.apiKeys, action.payload];
  state.lastCreatedApiKey = action.payload.key;
});

const updateApiKeysAsync = createAsyncReducers<
  ApiKeysState,
  ApiKeysRequestKey,
  ApiKey,
  { apiKey: ApiKeyConfig }
>("updateApiKeys", (state, action) => {
  state.apiKeys = state.apiKeys.map((c) =>
    c.id === action.payload.id ? action.payload : c,
  );
});

const deleteApiKeysAsync = createAsyncReducers<
  ApiKeysState,
  ApiKeysRequestKey,
  unknown,
  { id: string }
>("deleteApiKeys", (state, action) => {
  state.apiKeys = state.apiKeys.filter((c) => c.id !== action.payload);
});

const ApiKeysSlice = createSlice({
  name: "api-keys",
  initialState,
  reducers: {
    getApiKeysRequest: getApiKeysAsync.request,
    getApiKeysSuccess: getApiKeysAsync.success,
    getApiKeysFailure: getApiKeysAsync.failure,
    getRolesRequest: getRolessAsync.request,
    getRolesSuccess: getRolessAsync.success,
    getRolesFailure: getRolessAsync.failure,
    addApiKeysRequest: addApiKeysAsync.request,
    addApiKeysSuccess: addApiKeysAsync.success,
    addApiKeysFailure: addApiKeysAsync.failure,
    updateApiKeysRequest: updateApiKeysAsync.request,
    updateApiKeysSuccess: updateApiKeysAsync.success,
    updateApiKeysFailure: updateApiKeysAsync.failure,
    deleteApiKeysRequest: deleteApiKeysAsync.request,
    deleteApiKeysSuccess: deleteApiKeysAsync.success,
    deleteApiKeysFailure: deleteApiKeysAsync.failure,
    deleteLasCreatedtApiKey(state) {
      state.lastCreatedApiKey = undefined;
    },
  },
});

export const {
  getApiKeysRequest,
  getApiKeysSuccess,
  getApiKeysFailure,
  getRolesRequest,
  getRolesSuccess,
  getRolesFailure,
  addApiKeysRequest,
  addApiKeysSuccess,
  addApiKeysFailure,
  updateApiKeysRequest,
  updateApiKeysSuccess,
  updateApiKeysFailure,
  deleteApiKeysRequest,
  deleteApiKeysSuccess,
  deleteApiKeysFailure,
  deleteLasCreatedtApiKey,
} = ApiKeysSlice.actions;

const getApiKeysEpic = (action, _state, { ajax }) => {
  return action.pipe(
    ofType(getApiKeysRequest.type),
    mergeMap(() =>
      ajax.getJSON(`${baseURL}/keys`).pipe(
        map((apiKeys: ApiKey[]) => getApiKeysSuccess(apiKeys)),
        catchError((error) => {
          return of(
            getApiKeysFailure(error.message || "Error fetching api keys", {
              errorMessage: error.message || "Error fetching api keys",
            }),
          );
        }),
      ),
    ),
  );
};

const getApiKeysRolesEpic = (action, _state, { ajax }) => {
  return action.pipe(
    ofType(getRolesRequest.type),
    mergeMap(() =>
      ajax.getJSON(`${baseURL}/roles`).pipe(
        map((response: { roles: string[]; "X-Real-IP": string }) =>
          getRolesSuccess(response.roles),
        ),
        catchError((error) => {
          return of(
            getRolesFailure(error.message || "Error fetching api-keys roles", {
              errorMessage: error.message || "Error fetching api-keys roles",
            }),
          );
        }),
      ),
    ),
  );
};

const addApiKeysEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(addApiKeysRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          apiKey: ApiKeyConfig;
        }>,
      ) => {
        const { apiKey } = action.payload;
        return ajax({
          url: `${baseURL}/keys`,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: apiKey,
          responseType: "json",
        }).pipe(
          mergeMap((response: { response: any; message?: string }) =>
            of(addApiKeysSuccess(response.response)),
          ),
          catchError((error) =>
            of(
              addApiKeysFailure(
                error.response?.message ||
                  error.message ||
                  "Error updating configuration",
                {
                  errorMessage:
                    error.response?.message ||
                    error.message ||
                    "Error updating configuration",
                },
              ),
            ),
          ),
        );
      },
    ),
  );

const updateUserEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(updateApiKeysRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          apiKey: ApiKey;
        }>,
      ) => {
        const { apiKey } = action.payload;
        const url = `${baseURL}/keys/${apiKey?.id}`;

        return ajax({
          url,
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: apiKey,
          responseType: "json",
        }).pipe(
          mergeMap((data: { response: ApiKey }) =>
            of(
              updateApiKeysSuccess(data.response, {
                notificationOnSuccess: apiKeysControllerMessages.updateSuccess,
              }),
            ),
          ),
          catchError((error) =>
            of(
              updateApiKeysFailure(
                error.response?.message ||
                  error.message ||
                  "Error updating configuration",
                {
                  errorMessage:
                    error.response?.message ||
                    error.message ||
                    "Error updating configuration",
                },
              ),
            ),
          ),
        );
      },
    ),
  );

const deleteApiKeysEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(deleteApiKeysRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          id: string;
        }>,
      ) => {
        const { id } = action.payload;
        const url = `${baseURL}/keys/${id}`;

        return ajax({ url, method: "DELETE" }).pipe(
          mergeMap(() => of(deleteApiKeysSuccess(id))),
          catchError((error) =>
            of(
              deleteApiKeysFailure(
                error.response?.message ||
                  error.message ||
                  "Error updating configuration",
                {
                  errorMessage:
                    error.response?.message ||
                    error.message ||
                    "Error updating configuration",
                },
              ),
            ),
          ),
        );
      },
    ),
  );

export default {
  actions: ApiKeysSlice.actions,
  reducer: ApiKeysSlice.reducer,
  middleware: [
    getApiKeysEpic,
    getApiKeysRolesEpic,
    addApiKeysEpic,
    updateUserEpic,
    deleteApiKeysEpic,
  ],
  selectors: ApiKeysSlice.selectors,
};
